import type { Dictionary, Translator } from '@/features/i18n/types';

/**
 * Interpolates template strings with parameters
 * Replaces {paramName} with actual values
 *
 * @param template - The template string with placeholders
 * @param params - Object with parameter values
 * @returns The interpolated string
 *
 * @example
 * ```typescript
 * interpolate('Hello {name}!', { name: 'World' }) // "Hello World!"
 * interpolate('Total: {count} items', { count: 5 }) // "Total: 5 items"
 * ```
 */
export function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(.*?)\}/g, (_, key) => String(params[key.trim()] ?? ''));
}

/**
 * Creates a Translator function from a Dictionary
 * Use this in server components to get the same t() API as client components
 *
 * @param dictionary - The dictionary object with translation key-value pairs
 * @returns A Translator function that can translate keys with parameter support
 *
 * @example
 * ```typescript
 * // In a server component
 * import { getMergedDictionary } from '@/features/i18n/server/dictionary';
 * import { createTranslator } from '@/features/i18n/lib/translator';
 *
 * export async function MyServerComponent({ locale }: { locale: Locale }) {
 *   const dict = await getMergedDictionary(locale);
 *   const t = createTranslator(dict);
 *
 *   return <h1>{t('welcome.title', { name: 'User' })}</h1>;
 * }
 * ```
 */
export function createTranslator(dictionary: Dictionary): Translator {
  return (key: string, params?: Record<string, string | number>) => {
    const str = dictionary[key] ?? key;
    return interpolate(str, params);
  };
}
